import React from 'react';
import { AuditMetrics, ConfidenceBucket } from '../../../types';
import { KPI, Th, Td, tbStyle } from './AuditTablePrimitives';
import { pct, r2, colorR, colorWR } from '../utils/formatters';

export function MetricsCards({ metrics, auditCount }: { metrics: AuditMetrics; auditCount: number }) {
  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <KPI label="Total"      value={String(metrics.total)} />
        <KPI label="Closed"     value={String(metrics.closed)} />
        <KPI label="Win Rate"   value={pct(metrics.winRate)}   color={colorWR(metrics.winRate)} />
        <KPI label="Avg R"      value={r2(metrics.avgR)}        color={colorR(metrics.avgR)} />
        <KPI label="Median R"   value={r2(metrics.medianR)}     color={colorR(metrics.medianR)} />
        <KPI label="Expectancy" value={r2(metrics.expectancy)}  color={colorR(metrics.expectancy)} />
        <KPI label="Conf Cal."  value={metrics.confidenceCalibrated ? '✓' : '✗'}
          color={metrics.confidenceCalibrated ? 'var(--green)' : 'var(--red)'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {(['continuation','reversal'] as const).map(k => {
          const d = metrics.continuationVsReversal[k];
          return (
            <div key={k} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--t3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.5px' }}>{k}</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div><div style={{ fontSize: 9, color: 'var(--t3)' }}>Total</div><div style={{ fontSize: 16, fontWeight: 700 }}>{d.total}</div></div>
                <div><div style={{ fontSize: 9, color: 'var(--t3)' }}>Win Rate</div><div style={{ fontSize: 16, fontWeight: 700, color: colorWR(d.winRate) }}>{pct(d.winRate)}</div></div>
                <div><div style={{ fontSize: 9, color: 'var(--t3)' }}>Avg R</div><div style={{ fontSize: 16, fontWeight: 700, color: colorR(d.avgR) }}>{r2(d.avgR)}</div></div>
                <div><div style={{ fontSize: 9, color: 'var(--t3)' }}>Median R</div><div style={{ fontSize: 14, fontWeight: 700, color: colorR(d.medianR) }}>{r2(d.medianR)}</div></div>
                <div><div style={{ fontSize: 9, color: 'var(--t3)' }}>Expectancy</div><div style={{ fontSize: 14, fontWeight: 700, color: colorR(d.expectancy) }}>{r2(d.expectancy)}</div></div>
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--t2)', marginBottom: 6 }}>By Setup Type</div>
        <table style={tbStyle}>
          <thead><tr><Th>Setup Type</Th><Th right>Count</Th><Th right>Win Rate</Th><Th right>Avg R</Th></tr></thead>
          <tbody>
            {Object.entries(metrics.bySetupType).sort((a,b) => b[1].count - a[1].count).map(([t, s]) => (
              <tr key={t}>
                <Td><span style={{ fontSize: 10 }}>{t}</span></Td>
                <Td right>{s.count}</Td>
                <Td right color={colorWR(s.winRate)}>{pct(s.winRate)}</Td>
                <Td right color={colorR(s.avgR)}>{r2(s.avgR)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--t2)', marginBottom: 6 }}>Confidence Calibration</div>
        <table style={tbStyle}>
          <thead><tr><Th>Bucket</Th><Th right>Count</Th><Th right>Win Rate</Th><Th right>Avg R</Th><Th>Calibration</Th></tr></thead>
          <tbody>
            {(['0-39','40-54','55-69','70-84','85+'] as ConfidenceBucket[]).map(b => {
              const s = metrics.byConfidenceBucket[b];
              return (
                <tr key={b}>
                  <Td>{b}</Td>
                  <Td right>{s.count}</Td>
                  <Td right color={colorWR(s.winRate)}>{pct(s.winRate)}</Td>
                  <Td right color={colorR(s.avgR)}>{r2(s.avgR)}</Td>
                  <Td><span style={{ fontSize: 10, color: s.calibration.startsWith('✓') ? 'var(--green)' : s.calibration.startsWith('✗') ? 'var(--red)' : 'var(--t3)' }}>{s.calibration}</span></Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--t2)', marginBottom: 6 }}>Trend Alignment</div>
        <table style={tbStyle}>
          <thead><tr><Th>Alignment</Th><Th right>Count</Th><Th right>Win Rate</Th><Th right>Avg R</Th></tr></thead>
          <tbody>
            {(['aligned_with_trend','counter_trend','neutral_trend_context'] as const).map(k => {
              const s = metrics.byTrendAlignment[k];
              return (
                <tr key={k}>
                  <Td color={k === 'aligned_with_trend' ? 'var(--green)' : k === 'counter_trend' ? 'var(--red)' : 'var(--t3)'}>
                    <span style={{ fontSize: 10 }}>{k.replace(/_/g,' ')}</span>
                  </Td>
                  <Td right>{s.count}</Td>
                  <Td right color={colorWR(s.winRate)}>{pct(s.winRate)}</Td>
                  <Td right color={colorR(s.avgR)}>{r2(s.avgR)}</Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--t2)', marginBottom: 6 }}>By Market Phase</div>
        <table style={tbStyle}>
          <thead><tr><Th>Phase</Th><Th right>Count</Th><Th right>Win Rate</Th><Th right>Avg R</Th></tr></thead>
          <tbody>
            {Object.entries(metrics.byPhase).map(([p, s]) => (
              <tr key={p}>
                <Td><span style={{ fontSize: 10 }}>{p}</span></Td>
                <Td right>{s.count}</Td>
                <Td right color={colorWR(s.winRate)}>{pct(s.winRate)}</Td>
                <Td right color={colorR(s.avgR)}>{r2(s.avgR)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(metrics.bestSetupFamilies.length > 0 || metrics.worstSetupFamilies.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[{ label: '🏆 Best Setup Families', data: metrics.bestSetupFamilies },
            { label: '⚠ Weakest Setup Families', data: metrics.worstSetupFamilies }].map(({ label, data }) => (
            <div key={label} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--t2)', marginBottom: 6 }}>{label}</div>
              {data.map(f => (
                <div key={f.setupType} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0',
                  borderBottom: '1px solid var(--border)22', fontSize: 10 }}>
                  <span style={{ color: 'var(--t2)' }}>{f.setupType.substring(0, 24)}</span>
                  <span style={{ color: colorR(f.expectancy), fontFamily: 'var(--mono)', fontWeight: 700 }}>
                    {r2(f.expectancy)} ({f.count})
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 4 }}>
        Metrics computed from {metrics.closed} closed records. Win rate calibration based on confidence buckets.
      </div>
    </>
  );
}
